/**
 * Things in the road.
 *
 * The ramp already made the argument this file generalises, in its own comment: a prop may be steeper than
 * the terrain clamp *because its shape is authored and every way it can go wrong is bounded*. That is worth
 * having for more than one shape, so here is the bound, once, for all of them.
 *
 * **Everything she can drive over is a shallow convex mound.** What differs between a log, a rock, a hay bale
 * and a speed bump is entirely what it looks like - the collision shape is the same idea at different
 * proportions. That is not a shortcut; it is the only way to add variety to the road without adding a new way
 * to get stuck each time. A log with a steep face is a log that stops a three-year-old, and she cannot tell
 * by looking which one she has been given.
 *
 * The one exception is the ramp, whose launch face is deliberately vertical - it is the *far* side, so it
 * throws the car forwards and can never be climbed into.
 *
 * Pure geometry in metres. `src/drive/props.ts` turns these outlines into Box2D bodies and pictures.
 */

/** Things with collision. */
export const PROP_KINDS = ['ramp', 'log', 'rock', 'bale', 'bump'] as const;
export type PropKind = (typeof PROP_KINDS)[number];

/** Things with no collision at all: pure delight, nothing to catch on. */
export const DECOR_KINDS = ['overpass', 'gate', 'signpost', 'bunting'] as const;
export type DecorKind = (typeof DECOR_KINDS)[number];

export interface PropSpec {
  readonly kind: PropKind;
  /** Where its near foot sits, in metres along the route. */
  readonly atM: number;
  readonly lengthM: number;
  readonly heightM: number;
}

export interface DecorSpec {
  readonly kind: DecorKind;
  readonly atM: number;
  readonly lengthM: number;
  readonly heightM: number;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * The steepest face any prop may present to an approaching wheel.
 *
 * Comfortably under the breakover angle of every vehicle in the game - the hovercraft's 0.27m wheels are the
 * limiting case - so no prop can beach a car or stop it dead. A test drives that vehicle over every kind.
 */
export const MAX_PROP_SLOPE = 0.45;

/**
 * Tallest a prop may be.
 *
 * A comfort limit now rather than a geometric one: at 0.45m a hay bale is about a third of the height of the car
 * she is driving, which is a substantial thing to climb over and about as much as reads as fun rather than as an
 * obstruction.
 */
export const MAX_PROP_HEIGHT_M = 0.5;

/**
 * The steepest face a *mound* may present.
 *
 * Separate from `MAX_PROP_SLOPE`, and much steeper, and the reason is a lesson learnt from three attempts at
 * drawing one. At 0.45 a mound is seven times longer than it is tall, and nothing at 7:1 looks like a log, a
 * rock or a bale - so the picture either lied about the collision shape or the object was unrecognisable.
 *
 * 1.0 is 45 degrees, which sounds alarming and is not, for a reason specific to these shapes: the face is only
 * a quarter of a metre long. Breakover is about a *hull* grounding across a crest, and needs a slope that
 * persists for half a wheelbase; a knuckle of this size is climbed by the wheel and never touched by the belly.
 *
 * Whether a wheel manages it is not something arithmetic settles, so it is settled by driving. The hovercraft -
 * 0.27m wheels, the smallest in the game - is driven over four of every kind at maximum height, and over three
 * of each at the steepest points of the hilliest route, with the pedal simply held down. See vehicle-sim.
 */
export const MAX_MOUND_SLOPE = 1;

/**
 * How much of a beam's length each chamfer takes, and how long a beam is for its height.
 *
 * These two numbers together decide whether a single log is drivable, and it took two tries to find a pair that
 * is. A chamfer of 0.16 at the mound's 3:1 gave a 64-degree face and the sim stopped the hovercraft dead against
 * it. Lengthening to 4.5:1 brought the face to 45 degrees - the angle the mounds are allowed - and it *still*
 * stopped, which was the useful surprise.
 *
 * A mound gets away with 45 as its *bound* because only a short knuckle near the top reaches it: the face a wheel
 * actually meets first is nearer 30 degrees, and by the time the slope steepens the wheel is already climbing. A
 * beam presents its full angle from the first contact and then asks the car to hold a plateau.
 *
 * Chamfering harder fixed it and cost the look: at 0.3 the flat top is only 40% of the length and the thing reads
 * as a wedge rather than a log. Being lower did not help on its own either - a 0.30m beam with a 51-degree bevel
 * still stopped the car.
 *
 * The pair that works is 0.24 with the height brought down to 0.24-0.34m: a 43-degree bevel, a flat top of just
 * over half the length, and a log that is knee-high, which is what a log lying in a road is anyway. Four
 * combinations were tried and the sim rejected three of them; none of that was arguable from the geometry.
 */
export const BEAM_CHAMFER = 0.24;
export const BEAM_LENGTH_PER_HEIGHT = 4.5;



/**
 * How long a mound must be for its height.
 *
 * The first face rises 0.62 of the height over 0.22 of the length, so its slope is 2.82·h/L. Setting that at
 * MAX_MOUND_SLOPE gives the ratio below, with a little margin. Every kind's proportions are derived from this
 * rather than chosen, which is why none of them can be authored into a wall by accident - and why making them
 * chunkier was a matter of changing one bound rather than redrawing anything.
 */
export const MOUND_LENGTH_PER_HEIGHT = 2.82 / MAX_MOUND_SLOPE + 0.25;

/** Proportions per kind, in metres. Lengths come from the ratio above, never from taste. */
export const PROP_SIZES: Readonly<Record<PropKind, { readonly minHeightM: number; readonly maxHeightM: number }>> =
  {
    // A ramp is not a mound; its size is governed by the wheelbase it has to be longer than.
    ramp: { minHeightM: 0.85, maxHeightM: 1.35 },
    /*
     * Height is the only free number - length follows from it at three-to-one - so a taller obstacle is a longer
     * one and its faces keep exactly the same angles. Raised twice, both times after looking at them on screen
     * rather than by argument, and the drivability sim re-run at each new maximum: four of every kind in a row,
     * and three of each at the steepest points of the hilliest route.
     */
    log: { minHeightM: 0.24, maxHeightM: 0.34 },
    rock: { minHeightM: 0.3, maxHeightM: 0.44 },
    bale: { minHeightM: 0.38, maxHeightM: 0.5 },
    bump: { minHeightM: 0.13, maxHeightM: 0.2 },
  };

/** The steepest face a beam presents. Derived from the chamfer and the length ratio, not chosen. */
export const MAX_BEAM_SLOPE = 1 / (BEAM_CHAMFER * BEAM_LENGTH_PER_HEIGHT);

/** The length a mound of this height must have. Ramps carry their own length instead. */
export function moundLengthFor(heightM: number): number {
  return heightM * MOUND_LENGTH_PER_HEIGHT;
}

/**
 * How long a prop of this kind and height has to be.
 *
 * Every kind's length follows from its height, and the ratio is whatever keeps its faces at the angle the
 * drivability sims have proven. Nothing here is a matter of taste, which is why a log could be made to look
 * right by changing one number instead of by redrawing it.
 */
export function propLengthFor(kind: PropKind, heightM: number): number {
  if (kind === 'log') return heightM * BEAM_LENGTH_PER_HEIGHT;
  return heightM * MOUND_LENGTH_PER_HEIGHT;
}

/**
 * A prop's outline in world metres, counter-clockwise, sitting on `groundY`.
 *
 * Convex and six or fewer points for every kind, so Box2D takes it as a single polygon with no decomposition
 * and nothing to catch on between pieces.
 */
export function propOutline(
  spec: PropSpec,
  groundY: number,
  facing: 1 | -1 = 1,
  slope = 0,
  sinkM = 0,
): readonly Point[] {
  // A ramp is a built thing and is only ever placed on level ground, so it never needs tilting.
  if (spec.kind === 'ramp') return rampOutline(spec, groundY, facing);
  // A log is one solid beam, so its shape is a rectangle with the corners knocked off, not a dome.
  if (spec.kind === 'log') return beamOutline(spec, groundY, slope, sinkM);
  return moundOutline(spec, groundY, slope, sinkM);
}

/**
 * A mound: up, along the top, and down again.
 *
 * Symmetrical on purpose. She drives this route in both directions, and a shape that is gentle one way and
 * abrupt the other would be a different obstacle on the way home - which is exactly the kind of asymmetry that
 * makes a game feel arbitrary.
 *
 * **Rotated to lie along the ground it sits on**, and then sunk by however much is needed to close any gap left
 * under the middle. A mound built on a flat base and dropped onto a hillside either floats at one end or is
 * swallowed at the other, and both look like the world is made of separate pieces.
 *
 * A *rotation* rather than a shear: a rotation is rigid, so the shape keeps its proportions and stays convex,
 * and its faces keep exactly the angles they were designed with **relative to the ground beneath them**. What
 * changes is the absolute slope a wheel meets, which becomes the mound's own angle plus the hill's - and that
 * sum is what the drivability sims check, on the steepest ground the terrain clamp allows.
 */
function moundOutline(
  spec: PropSpec,
  groundY: number,
  slope: number,
  sinkM: number,
): readonly Point[] {
  const { lengthM: length, heightM: height } = spec;

  // The mound in its own frame: base along y = 0, running from x = 0 to x = length.
  const local: readonly Point[] = [
    { x: 0, y: 0 },
    { x: length * 0.22, y: height * 0.62 },
    { x: length * 0.42, y: height },
    { x: length * 0.58, y: height },
    { x: length * 0.78, y: height * 0.62 },
    { x: length, y: 0 },
  ];

  /*
   * Rotated so its base lies along the chord between the ground at each end. The length is measured along the
   * *slope*, so a mound on a hill covers slightly less ground than one on the flat - which is correct, and is
   * what stops it stretching as the hill steepens.
   */
  const hypotenuse = Math.sqrt(1 + slope * slope);
  const cos = 1 / hypotenuse;
  const sin = slope / hypotenuse;

  return local.map((point) => ({
    x: spec.atM + point.x * cos - point.y * sin,
    y: groundY + point.x * sin + point.y * cos - sinkM,
  }));
}

/**
 * A beam: a rectangle with its bottom corners chamfered.
 *
 * A single log lying across the road is a rectangle from the side, and drawing it as anything else was the
 * mistake behind five earlier versions of it. The chamfers are what make it drivable at all - a truly vertical
 * face of this height cannot be climbed by a 0.27m wheel at any torque - and they are short enough that the
 * shape still reads as one solid piece of timber rather than a hump.
 *
 * The faces are steep: about 64 degrees. That is far outside anything else in the game and is allowed for one
 * reason only, which is that the hovercraft is driven over four of them at maximum height in `vehicle-sim` and
 * gets across. If that ever stops being true, this shape has to get longer.
 */
function beamOutline(
  spec: PropSpec,
  groundY: number,
  slope: number,
  sinkM: number,
): readonly Point[] {
  const { lengthM: length, heightM: height } = spec;
  const chamfer = length * BEAM_CHAMFER;

  const local: readonly Point[] = [
    { x: 0, y: 0 },
    { x: chamfer, y: height },
    { x: length - chamfer, y: height },
    { x: length, y: 0 },
  ];

  const hypotenuse = Math.sqrt(1 + slope * slope);
  const cos = 1 / hypotenuse;
  const sin = slope / hypotenuse;
  return local.map((point) => ({
    x: spec.atM + point.x * cos - point.y * sin,
    y: groundY + point.x * sin + point.y * cos - sinkM,
  }));
}

/**
 * A ramp: a shallow climb to a blunt lip, and a vertical drop off the far side.
 *
 * The drop is the point - it throws the car forwards rather than presenting anything to climb - and the lip is
 * blunted to a short flat rather than left as a knife edge a wheel could clip.
 *
 * **Facing mirrors it in place; it never moves it.** The ramp always occupies `[atM, atM + lengthM]`, which is
 * the span route-gen certified level and the span `propFootprint` reserves. Facing used to run the shape
 * *backwards* from `atM`, onto ground nobody had certified - up to the terrain clamp's 0.30 against the ramp's
 * own 0.08 limit, which over four metres is about a metre of error, near enough the whole height of the thing.
 * The fixture is built from this outline, so on the way home the ramp was buried in a rise or hanging over a
 * dip, metres from where she had just driven over it. See the invariant at `src/core/world/route.ts`.
 */
function rampOutline(spec: PropSpec, groundY: number, facing: 1 | -1): readonly Point[] {
  const foot = facing > 0 ? spec.atM : spec.atM + spec.lengthM;
  const lip = facing > 0 ? spec.atM + spec.lengthM : spec.atM;
  const blunt = spec.lengthM * 0.12 * facing;

  const points = [
    { x: foot, y: groundY },
    { x: lip, y: groundY },
    { x: lip, y: groundY + spec.heightM },
    { x: lip - blunt, y: groundY + spec.heightM },
  ];

  // Planck wants counter-clockwise winding; a left-facing ramp comes out the other way round.
  return facing > 0 ? points : [...points].reverse();
}

export interface Face {
  readonly slope: number;
  /** True for the one face a ramp launches off, which is allowed to be vertical. */
  readonly isLaunch: boolean;
}

/**
 * Every face of an outline, with its slope.
 *
 * The launch face of a ramp is the vertical one at its lip. It is exempt because a wheel never meets it going
 * forwards: it is the edge the car leaves the ground from.
 */
export function propFaces(outline: readonly Point[]): readonly Face[] {
  const faces: Face[] = [];
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % outline.length]!;
    const run = b.x - a.x;
    const rise = b.y - a.y;
    if (Math.abs(run) < 1e-6) {
      faces.push({ slope: Number.POSITIVE_INFINITY, isLaunch: true });
      continue;
    }
    faces.push({ slope: Math.abs(rise / run), isLaunch: false });
  }
  return faces;
}

/**
 * How much steeper than the ground a prop's own faces are.
 *
 * The bound that `MAX_PROP_SLOPE` states, measured in the shape's own frame. Pass the slope of the ground it
 * sits on and this removes it again, which is what makes the guarantee checkable on a hillside as well as on
 * the flat.
 */
export function slopeAboveGround(outline: readonly Point[], groundSlope: number): number {
  const angle = Math.atan(groundSlope);
  const cos = Math.cos(-angle);
  const sin = Math.sin(-angle);
  const levelled = outline.map((point) => ({
    x: point.x * cos - point.y * sin,
    y: point.x * sin + point.y * cos,
  }));
  return maxClimbableSlope(levelled);
}

/** The steepest face a wheel can actually meet. Infinity means a vertical face, which only ramps have. */
export function maxClimbableSlope(outline: readonly Point[]): number {
  let steepest = 0;
  for (const face of propFaces(outline)) {
    if (face.isLaunch) continue;
    steepest = Math.max(steepest, face.slope);
  }
  return steepest;
}

/** Convex in the sense Box2D needs: every turn the same way round. */
export function isConvex(outline: readonly Point[]): boolean {
  if (outline.length < 3) return false;
  let sign = 0;
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % outline.length]!;
    const c = outline[(i + 2) % outline.length]!;
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) < 1e-9) continue;
    const here = cross > 0 ? 1 : -1;
    if (sign === 0) sign = here;
    else if (here !== sign) return false;
  }
  return true;
}

/** How much room a prop needs along the route, including the clear ground either side of it. */
export function propFootprint(spec: PropSpec): { readonly fromM: number; readonly toM: number } {
  const clear = spec.kind === 'ramp' ? 6 : 1.5;
  return { fromM: spec.atM - clear, toM: spec.atM + spec.lengthM + clear };
}
